import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { TargetedPorchSettings } from "building-types/porches/porch";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { Table } from "common/data/table";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { addPolygonsToBSP, buildPerimeterPolygons, buildPolygonsFromPolyline2 } from "geom/bsp";
import type { NodeBSP } from "geom/bspTypes";
import { planeNewXY, planeReverse } from "geom/plane";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2Copy, polyLine2ReflectOverYAxis, polyLine2ReverseCopy, polyLine2SetCW } from "geom/polyLine2";
import type { Vec2 } from "geom/vec2";

export function planarGraphVerticesToPoints(
  floorAbovePlanarGraph: PolygonPlanarGraph | undefined,
  unsupportedPerimeterVertices: number[],
) {
  const unsupportedPerimeterPoints: Vec2[] = [];
  if (floorAbovePlanarGraph) {
    for (const v of unsupportedPerimeterVertices) {
      const x = floorAbovePlanarGraph.planarGraph.vertexPositions[v * 2];
      const y = floorAbovePlanarGraph.planarGraph.vertexPositions[v * 2 + 1];
      unsupportedPerimeterPoints.push([x, y]);
    }
  }
  return unsupportedPerimeterPoints;
}

export function collectPorchRooms(
  startRoom: Room,
  roomIndex: RoomIndex,
  visitedPorchRooms: { [key: number]: boolean },
  targetedPorchSettings: TargetedPorchSettings,
): Room[] {
  const porchRooms: Room[] = [];

  const isPorchSettingOverridden = (room: Room) => {
    const roomId = getSourceRoomId(room);
    return targetedPorchSettings[roomId]?.excludeFromPorchMerge;
  };

  roomIndex.traverseRooms(
    startRoom,
    (room, adjRoom) => {
      if (room.type !== RoomType.FRONT_PORCH && room.type !== RoomType.REAR_PORCH) {
        return false;
      }

      if (room.type !== adjRoom.type) {
        return false;
      }

      if (isPorchSettingOverridden(room) || isPorchSettingOverridden(adjRoom)) {
        return false;
      }

      return true;
    },
    (room) => porchRooms.push(room),
    visitedPorchRooms,
  );

  return porchRooms;
}

export function buildInsideRoomBSP(rooms: Table<Room>): NodeBSP | null {
  let insideRoomBSP: NodeBSP | null = null;

  for (const roomId in rooms) {
    const { attributes, outline, level, ceilingHeightOffset } = rooms[roomId];

    // discard exterior rooms
    if (!isInteriorRoomEnclosure[attributes.roomEnclosure]) {
      continue;
    }

    // build the room volume polygons
    const { floorElevation, topOfWall } = level;
    const ceilingHeight = topOfWall + ceilingHeightOffset;

    const bottomPlane = planeNewXY(0, 0, floorElevation);
    const topPlane = planeNewXY(0, 0, floorElevation + ceilingHeight);

    const capPerimeter = polyLine2SetCW(polyLine2Copy(outline));

    const polygons = buildPerimeterPolygons(outline, bottomPlane, ceilingHeight);
    buildPolygonsFromPolyline2(
      polyLine2ReflectOverYAxis(polyLine2ReverseCopy(capPerimeter)),
      planeReverse(bottomPlane),
      0,
      polygons,
    );
    buildPolygonsFromPolyline2(capPerimeter, topPlane, 0, polygons);

    // add the room volume polygons to the BSP
    insideRoomBSP = addPolygonsToBSP(insideRoomBSP, null, polygons);
  }

  return insideRoomBSP;
}
